import React, { useState, useEffect } from 'react';
import { Container, Grid, TextField, Button, Box, Typography, Paper, Stack, Avatar, Divider, InputAdornment, MenuItem } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Phone, Email, LocationOn, Print, Send, Person, Subject, Message, AccessTime, Category } from '@mui/icons-material';
import api from '../utils/api';
import { snackbar, successMessage } from '../utils/showMessage';

const Contact = () => {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', category: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({ phones: [], fax: '', email: '', address: '', hours: '' });

  const theme = useTheme();
  const accent1 = theme.palette.primary.main;
  const accent2 = theme.palette.primary.light || theme.palette.secondary.main;
  const chipBg = alpha(theme.palette.primary.main, 0.12);
  const chipBgMuted = alpha(theme.palette.text.primary, 0.04);

  const sanitizePhone = (s) => {
    if (!s) return '';
    // keep leading + and digits only
    return String(s).trim().replace(/[^\d+]/g, '');
  };

  const sanitizeEmail = (e) => {
    if (!e) return '';
    return String(e).trim().replace(/\s+/g, '');
  };

  const categories = [
    { value: 'manager', label: 'ارتباط با مدیر' },
    { value: 'suggestions', label: 'پیشنهادات و انتقادات' },
    { value: 'business', label: 'درخواست اضافه شدن کسب و کار' }
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.subject || !form.message) {
      return snackbar('لطفاً نام، موضوع و پیام را وارد کنید.');
    }
    if (!form.phone && !form.email) {
      return snackbar('لطفاً شماره موبایل یا ایمیل را وارد کنید.');
    }
    if (!form.category) {
      return snackbar('لطفاً دسته‌بندی را انتخاب کنید.');
    }

    try {
      setLoading(true);
      try {
        const result = await api.post('/contact/submit', { ...form, source: 'app' });
         successMessage('پیام شما ارسال شد.');
      } catch (err) {

        console.error("err", err)
        snackbar('خطا در ذخیره پیام');

      }
      setForm({ name: '', phone: '', category: '', subject: '', message: '' });
    } catch (err) {
      console.error('Contact submit error', err);
      snackbar('ارسال پیام ناموفق بود.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/contact-info');
        if (res?.data) {
          const data = res.data;
          let phones = [''];
          if (Array.isArray(data.phones)) phones = data.phones;
          else if (typeof data.phone === 'string' && data.phone) phones = [data.phone];
          setContactInfo({ ...data, phones, fax: data.fax || '', map: data.map || data.mapLink || '' });
        }
      } catch (e) {

        snackbar('خطا در بارگذاری اطلاعات تماس از سرور.');
      }
    };
    load();
  }, []);

  return (
    <div className="main-container container">
      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">

              <Box>
                <Typography variant="h5">تماس با ما</Typography>
                <Typography variant="body2" color="text.secondary">هرگونه سوال، پیشنهاد یا درخواست همکاری را اینجا ارسال کنید. تیم ما ظرف چند روز پاسخ می‌دهد.</Typography>
              </Box>
            </Stack>

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="نام و نام خانوادگی"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Person color="action" /></InputAdornment>) }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="ایمیل"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Email color="action" /></InputAdornment>) }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="شماره تماس"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Phone color="action" /></InputAdornment>) }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    label="دسته‌بندی"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Category color="action" />
                        </InputAdornment>
                      )
                    }}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="موضوع"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Subject color="action" /></InputAdornment>) }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="پیام شما"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Message color="action" /></InputAdornment>) }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
                    <Button type="submit" variant="contained" size="large"
                      disabled={loading} startIcon={<Send style={{ paddingInlineEnd: 12 }} />}>
                      {loading ? 'در حال ارسال...' : 'ارسال پیام'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">پاسخ معمولاً طی 2-3 روز کاری ارسال می‌شود.</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 1, position: 'relative', overflow: 'hidden', transition: 'transform .22s ease, box-shadow .22s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: 6 } }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: `linear-gradient(180deg, ${accent1}, ${accent2})` }} />
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(accent1, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Phone sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box style={{ paddingInlineStart: 12 }}>
                      <Typography variant="h6">اطلاعات تماس</Typography>
                      <Typography variant="body2" color="text.secondary">راه‌های ارتباطی سریع با ما</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                {(contactInfo.phones || []).filter(Boolean).map((p, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ py: 1, px: 1.2, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Phone sx={{ color: 'primary.main', fontSize: 18 }} /></Box>
                    <a href={`tel:${sanitizePhone(p)}`} style={{ paddingInlineStart: 12, textDecoration: 'none', color: 'inherit', fontWeight: 600 }}>{p}</a>
                  </Stack>
                ))}

                {contactInfo.fax && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, px: 1.2, borderRadius: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: chipBgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Print sx={{ color: 'text.secondary', fontSize: 18 }} /></Box>
                    <span style={{ paddingInlineStart: 12 }}>{contactInfo.fax}</span>
                  </Stack>
                )}

                {contactInfo.email && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, px: 1.2, borderRadius: 1 }}>
                    <Box style={{ paddingInlineStart: 12 }} sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Email sx={{ color: 'primary.main', fontSize: 18 }} /></Box>
                    <a href={`mailto:${sanitizeEmail(contactInfo.email)}`} style={{ textDecoration: 'none', color: 'inherit' }}>{contactInfo.email}</a>
                  </Stack>
                )}

                {contactInfo.address && (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 1, px: 1.2, borderRadius: 1 }}>
                    <Box sx={{ width: 56, height: 36, borderRadius: 1.5, bgcolor: chipBgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </Box>
                    <Typography style={{ paddingInlineStart: 12 }} variant="body2">{contactInfo.address}</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>

            {contactInfo.hours && (
              <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 1, position: 'relative', overflow: 'hidden', transition: 'transform .22s ease, box-shadow .22s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: 6 } }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: `linear-gradient(180deg, ${accent2}, ${theme.palette.secondary ? theme.palette.secondary.main : accent2})` }} />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ marginInlineEnd: 2, width: 36, height: 36, borderRadius: 2, bgcolor: alpha(accent2, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccessTime sx={{ color: 'primary.main', fontSize: 22 }} />
                  </Box>
                  <Box style={{ paddingInlineStart: 12 }}>
                    <Typography variant="subtitle1">ساعات کاری</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{contactInfo.hours}</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {(() => {
              const mapSrc = contactInfo.map && String(contactInfo.map).trim() ? contactInfo.map : (contactInfo.address ? `https://maps.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&z=13&ie=UTF8&iwloc=&output=embed` : '');
              return mapSrc ? (
                <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', right: 12, top: 12, bgcolor: theme.palette.background.paper, px: 1.2, py: 0.5, borderRadius: 1.5, boxShadow: 1, fontSize: 12, zIndex: 2 }}>ما را روی نقشه ببینید</Box>
                    <iframe title="map" src={mapSrc} style={{ width: '100%', height: 260, border: 0, display: 'block' }} />
                  </Box>
                </Paper>
              ) : null;
            })()}
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default Contact;
